import { useMemo } from "react";

interface Product {
  category: string;
  affiliateLink: string;
}

interface Offer {
  affiliateLink?: string;
}

interface AffiliateCoverageProps {
  products: Product[];
  offers: Offer[];
}

function CoverageRing({ percent, color }: { percent: number; color: string }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (percent / 100) * circ;

  return (
    <svg width="72" height="72" viewBox="0 0 72 72">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#f3f4f6" strokeWidth="7" />
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="7"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 36 36)"
      />
      <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="700" fill="#111827">
        {percent}%
      </text>
    </svg>
  );
}

export default function AffiliateCoverage({ products, offers }: AffiliateCoverageProps) {
  const byCategory = useMemo(() => {
    const map: Record<string, { total: number; withLink: number }> = {};

    products.forEach((product) => {
      if (!map[product.category]) map[product.category] = { total: 0, withLink: 0 };
      map[product.category].total += 1;
      if (product.affiliateLink && product.affiliateLink !== "#" && product.affiliateLink.startsWith("http")) {
        map[product.category].withLink += 1;
      }
    });

    return Object.entries(map)
      .map(([cat, { total, withLink }]) => ({
        cat,
        total,
        withLink,
        pct: total > 0 ? Math.round((withLink / total) * 100) : 0,
      }))
      .sort((a, b) => b.pct - a.pct);
  }, [products]);

  const totalProducts = products.length;
  const withLink = products.filter(
    (product) => product.affiliateLink && product.affiliateLink !== "#" && product.affiliateLink.startsWith("http")
  ).length;
  const withoutLink = totalProducts - withLink;

  const offersWithLink = offers.filter(
    (offer) => offer.affiliateLink && offer.affiliateLink !== "#" && offer.affiliateLink.startsWith("http")
  ).length;
  const offersCoverage = offers.length > 0 ? Math.round((offersWithLink / offers.length) * 100) : 0;

  const overallPct = totalProducts > 0 ? Math.round((withLink / totalProducts) * 100) : 0;
  const ringColor = overallPct >= 80 ? "#10b981" : overallPct >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div>
      <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100">
        <CoverageRing percent={overallPct} color={ringColor} />
        <div>
          <p className="text-sm font-bold text-gray-900">Cobertura Geral</p>
          <p className="text-xs text-gray-400 mt-0.5">
            <span className="text-emerald-600 font-semibold">{withLink}</span> com link ·{" "}
            <span className="text-red-500 font-semibold">{withoutLink}</span> sem link
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Ofertas do Dia: <span className="font-semibold text-gray-700">{offersCoverage}%</span> cobertas
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {byCategory.slice(0, 6).map((item) => {
          const color = item.pct === 100 ? "bg-emerald-500" : item.pct >= 60 ? "bg-amber-400" : "bg-red-400";
          const textColor = item.pct === 100 ? "text-emerald-600" : item.pct >= 60 ? "text-amber-600" : "text-red-500";

          return (
            <div key={item.cat}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700 truncate max-w-[130px]">{item.cat}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">{item.withLink}/{item.total}</span>
                  <span className={`text-xs font-bold ${textColor}`}>{item.pct}%</span>
                </div>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${color}`}
                  style={{ width: `${item.pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {withoutLink > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-start gap-2 bg-red-50 rounded-xl p-3">
            <div className="w-5 h-5 flex items-center justify-center text-red-500 flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-base"></i>
            </div>
            <p className="text-xs text-red-600 leading-relaxed">
              <span className="font-bold">{withoutLink} produto{withoutLink !== 1 ? "s" : ""}</span> sem link de afiliado válido. Corrija em{" "}
              <a href="/admin/links" className="underline font-semibold cursor-pointer">Links de Afiliado</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
